import { Alert, Button, Stack, TextField } from "@mui/material";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SizeClass, usePaneWidth } from "shared-ts-ui-responsive";
import { LoginFooter } from "./LoginFooter";
import { LoginStrategy } from "./LoginStrategy";
import { useResolveLoginStrategy } from "./useResolveLoginStrategy";
import { useDefaultReturnUrl } from "./useDefaultReturnUrl";
import { PropertiesCard } from "shared-ts-ui-properties-card";
import { resolveUnauthorizedFromUrl } from "./resolveUnauthorizedFromUrl";

export interface LoginFormUsingPageSubmitProps {
  username?: string | null;
  onRegisterNewUser: () => void;
  strategy?: LoginStrategy; // defaults to 'auto'
  returnUrl?: string; // defaults to current location
}

export const LoginFormUsingPageSubmit: FC<LoginFormUsingPageSubmitProps> = (
  props
) => {
  const { onRegisterNewUser } = props;
  const width = usePaneWidth();
  const { t } = useTranslation("shared-ts-ui-identity");
  const strategy = useResolveLoginStrategy(props.strategy);
  const defaultReturnUrl = useDefaultReturnUrl(props);
  const unauthorized = resolveUnauthorizedFromUrl();
  const [username, setUsername] = useState<string>(props.username ?? "");
  const search = typeof window !== "undefined" ? window.location.search : "";
  const qs = new URLSearchParams(search);
  const externalMobileAuth = qs.get("externalMobileAuth") === "1";
  const callbackScheme = qs.get("callback_scheme") ?? "";
  const shouldAutoRegister =
    qs.get("action") === "register" || qs.get("register") === "1";

  const baseAction =
    typeof window !== "undefined"
      ? `${window.location.origin}/identity/login`
      : "/identity/login";
  const actionParams = new URLSearchParams();
  if (externalMobileAuth) actionParams.set("externalMobileAuth", "1");
  if (qs.get("callback_scheme"))
    actionParams.set("callback_scheme", qs.get("callback_scheme")!);
  const actionWithFlags = `${baseAction}${
    actionParams.toString() ? `?${actionParams}` : ""
  }`;

  const handleNewUserClick = useCallback(
    () => onRegisterNewUser(),
    [onRegisterNewUser]
  );

  // Auto-trigger register flow when in external browser mode with explicit register action
  const didAutoRegisterRef = useRef(false);
  useEffect(() => {
    if (
      externalMobileAuth &&
      shouldAutoRegister &&
      !didAutoRegisterRef.current
    ) {
      didAutoRegisterRef.current = true;
      try {
        console.log(
          "[LoginFormUsingPageSubmit] Auto-invoking onRegisterNewUser due to action=register in external mobile auth mode"
        );
        onRegisterNewUser();
      } catch (e) {
        console.error(
          "[LoginFormUsingPageSubmit] Error auto-invoking onRegisterNewUser:",
          e
        );
      }
    }
  }, [externalMobileAuth, shouldAutoRegister, onRegisterNewUser]);

  return (
    <form
      id={"login-form"}
      name={"login"}
      method={"post"}
      action={actionWithFlags}
      autoComplete={"on"}
      onSubmit={(e) => {
        console.log("=== FORM SUBMIT EVENT ===");
        console.log("Action URL:", actionWithFlags);
        console.log("Form data:", {
          username: e.currentTarget["username"]?.value,
          password: e.currentTarget["password"]?.value ? "***" : "empty",
          returnUrl: e.currentTarget["returnUrl"]?.value,
        });
        // Don't prevent default - let native submission happen
      }}
    >
      <Stack useFlexGap sx={{ gap: 2 }}>
        <PropertiesCard
          header={{
            title: t("Log In.title"),
          }}
          sx={{
            justifyContent: "center",
          }}
          elevation={width === SizeClass.Compact ? 0 : undefined}
        >
          <TextField
            label={t("Email")}
            name={"username"}
            defaultValue={username}
            type={"email"}
            autoComplete={"username"}
            fullWidth
            slotProps={{
              htmlInput: {
                id: "username",
                inputMode: "email",
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: false,
              },
            }}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            label={t("Password")}
            name={"password"}
            defaultValue={""}
            type={"password"}
            autoComplete={"current-password"}
            fullWidth
            slotProps={{
              htmlInput: {
                id: "password",
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: false,
              },
            }}
          />
          <Stack
            direction={"row"}
            sx={{ width: "100%", justifyContent: "space-evenly" }}
          >
            <Button type={"submit"} color={"secondary"} variant={"outlined"}>
              {t("Log In")}
            </Button>
          </Stack>
        </PropertiesCard>
        {unauthorized && (
          <Alert severity={"warning"}>{t("Wrong username or password")}</Alert>
        )}
        <input type="hidden" name="returnUrl" value={defaultReturnUrl} />
        {externalMobileAuth && (
          <>
            <input
              type="hidden"
              name="externalMobileAuth"
              value={externalMobileAuth ? "1" : "0"}
            />
            {callbackScheme && (
              <input
                type="hidden"
                name="callback_scheme"
                value={callbackScheme}
              />
            )}
          </>
        )}
        <LoginFooter
          username={username}
          loginStrategy={strategy}
          onNewUser={handleNewUserClick}
        />
      </Stack>
    </form>
  );
};
